import { useState, useRef } from 'react'
import cx from 'classnames'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'
import { Formik, FormikProps, Form, Field } from 'formik'
import { capitalize } from 'lodash'

import { Button } from 'blaze-ui/Button'
import { Input } from 'almanac-ui/TextInputField/Input'

import { UserClient } from 'api/clients/UserClient'
import { trackContinueWithSignUp } from 'screens/LoginOrSignup/utils'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { LOGIN_PATH } from 'paths'

import Styles from './Registration.module.scss'
import { GoogleOAuthButton } from './GoogleOAuthButton/GoogleOAuthButton'
import { FacebookOAuthButton } from './FacebookOAuthButton/FacebookOAuthButton'
import { Email } from 'blaze-ui/icons/24'
import { RegistrationStepProps } from 'screens/Registration/types'
import useURLParams from 'hooks/useURLParams'
import { WhiteLabelLogo } from 'components/WhiteLabelLogo/WhiteLabelLogo'
import { useInvitedWorkspace } from 'hooks/useInvitedWorkspace'

interface FormValueType {
  email: string
}

type Props = {
  workspaceInviteToken?: string
  redirectTo?: string
  checkUserExists?: boolean
} & RegistrationStepProps

const validationSchema = yup.object().shape({
  email: yup
    .string()
    .required('Email is required')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/u, 'Invalid email address'),
})

const Registration = ({ onNext, workspaceInviteToken = '', redirectTo = '', checkUserExists = false }: Props) => {
  const [showEmailInput, setShowEmailInput] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isError, setIsError] = useState(false)
  const [, setSearchParams] = useURLParams({ pushNewState: false })
  const { workspace: invitedWorkspace } = useInvitedWorkspace(workspaceInviteToken || undefined)

  const formikRef = useRef<FormikProps<FormValueType>>(null)
  const history = useHistory()

  const verifyUserExists = async (email: string) => {
    if (!email.length) return

    const res = await UserClient.verifyUserExists(email, redirectTo, checkUserExists)
    if (res.data.success) {
      setSearchParams({ email: email })
      trackContinueWithSignUp()
      onNext()
    } else {
      setIsError(true)
    }
  }

  return (
    <MultiStepStepLayout>
      <div className={Styles.container}>
        <div className={Styles.logo}>
          <WhiteLabelLogo workspace={invitedWorkspace} width={108} height={30} />
        </div>
        <h1 className={Styles.title}>Your marketing autopilot starts here</h1>
        <div className={Styles.buttons}>
          <GoogleOAuthButton workspaceInviteToken={workspaceInviteToken} />
          <FacebookOAuthButton workspaceInviteToken={workspaceInviteToken} />

          {showEmailInput ? (
            <Formik
              innerRef={formikRef}
              initialValues={{ email: '' }}
              validationSchema={validationSchema}
              validateOnChange
              validateOnMount
              onSubmit={async ({ email }) => {
                setIsProcessing(true)
                await verifyUserExists(email)
                setIsProcessing(false)
              }}
            >
              {({ values, errors, touched }) => (
                <Form noValidate className={Styles.emailForm}>
                  <div className={cx(Styles.emailField)}>
                    <Field
                      id="email"
                      name="email"
                      as={Input}
                      type="email"
                      size="xl"
                      placeholder="Continue with Email"
                      autoFocus
                      isValid={!(touched.email && errors.email)}
                      className={Styles.emailInput}
                    />
                  </div>
                  {touched.email && errors.email && <FormErrorMessage>{capitalize(errors.email)}</FormErrorMessage>}

                  {values.email.length > 0 && (
                    <Button
                      aria-label="Submit email"
                      isDisabled={isProcessing || !!errors.email}
                      size="xl"
                      variant="primary"
                      fullWidth
                      onPress={() => formikRef.current?.handleSubmit()}
                    >
                      Continue
                    </Button>
                  )}
                </Form>
              )}
            </Formik>
          ) : (
            <Button
              variant="primary"
              size="xl"
              fullWidth
              useChildClass
              frontIcon={Email}
              onPress={() => setShowEmailInput(true)}
            >
              <div className={Styles.registrationButton}>Continue with Email</div>
            </Button>
          )}
          {isError && (
            <div className={Styles.errorContainer}>
              <FormErrorMessage>Something went wrong – please try again.</FormErrorMessage>
            </div>
          )}
        </div>
        <span className={Styles.alreadyHaveAccount}>
          Already have an account?{' '}
          <button className={Styles.login} data-test-id="switch-login" onClick={() => history.push(LOGIN_PATH)}>
            Login
          </button>
        </span>
        <div className={Styles.footerText}>
          By signing up, you agree to the <br />
          <a href="https://www.blaze.ai/terms" target="_blank" rel="noreferrer">
            Terms of Service →
          </a>{' '}
          and{' '}
          <a href="https://www.blaze.ai/privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy →
          </a>
          .
        </div>
      </div>
    </MultiStepStepLayout>
  )
}

export default Registration
